// RTableGroup: the unit of atomicity, snapshot, observation and composition.
// Owns one physical DAG; member RTables are scoped projections of it, so one
// group position is a consistent snapshot of all member tables.
//
// ACTIONS (see payload.ts for formats):
//
//   create
//     Genesis of a group instance. Carries: seed, `schemaRef` + pinned
//     `schemaVersion` (the deployed RSchema id and version; validating a
//     create resolves the schema at that version — a cross-DAG dependency at
//     genesis), optional `initialRows` (genesis fiat rows keyed by table, in
//     insert-op shape, no authoring: they root permission
//     delegation chains and carry identity public keys), optional `bindings`
//     (qualified-name resolution, group name -> group object id, fixed in
//     v1), optional `canDeploy` (predicate gating schema ref-advances,
//     'object' context: $author only, no subject row; fixed in v1), hash
//     algorithm.
//
//   row
//     Envelope written automatically by a member RTable's DagScope: wraps ONE
//     inner row op (insert / update / delete), tagged with its table name so
//     the table's scope filter picks it back out (the nested-RSet pattern).
//
//   bundle
//     Single-entry atomic multi-table write: `writes` is an ORDERED
//     `{ table, op }[]` (the bundle order, carried explicitly since entry
//     hashing sorts map keys); entry meta is tagged with every touched table
//     (each table's scope filter matches its slice). The parts hash, validate
//     and apply together and can never exist apart, even mid-sync. Op `i`'s FK
//     conditions hold at the sequential cut `at` ∪ the ops before it.
//
//   ref-advance (canonical mvt payload, checked non-strictly)
//     Advances the group's observed version of a referenced object:
//       - the RSchema ref: THE SCHEMA DEPLOY MOMENT, as a barrier. When the
//         group declares `canDeploy`, that predicate is derived from the
//         group's create payload (mandatory) and the op carries
//         author/signature as extra fields; the deploy signature is verified
//         at validation (against the group's own provider) and the predicate
//         is then evaluated against the verified author.
//       - a bound foreign group ref: cross-group FK / exists observation,
//         a barrier advance (see observe). The
//         dependent resolves `group.table` targets through the foreign group
//         at the observed version (resolveForeignTableView); a concurrent
//         observation revises the merged frontier like a deploy.
//
// The effective schema at a group position = the RSchema resolved at the
// create's pinned schemaVersion unioned with ref-advances at or below the
// position. Tables exist by schema: getTable(name) constructs the nested
// RTable on a TableScope projection (tableId = deriveTableId(groupId, name));
// there is no table creation op.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var _a;
import { json } from "@hyper-hyper-space/hhs3_json";
import { HASH_SHA256 } from "@hyper-hyper-space/hhs3_crypto";
import { dag, position } from "@hyper-hyper-space/hhs3_dag";
import { version, formatValidationFailure, validationFailure, ValidationRejectedError, } from "@hyper-hyper-space/hhs3_mvt";
import { RootScopedDag, NestedScopedDag } from "@hyper-hyper-space/hhs3_mvt";
import { isRefAdvancePayload, extractRefVersion, extractAuthor, prepareRefAdvance, createRefAdvanceMeta, createRefAdvancePayload, resolveRefVersionAtPosition, findConcurrentRefAdvanceBarriers, refVersionAtOrAbove, } from "@hyper-hyper-space/hhs3_mvt";
import { signPayload as signPayloadHelper } from "@hyper-hyper-space/hhs3_mvt";
import { splitTableRef } from "../rschema/payload.js";
import { RTableImpl } from "../rtable/rtable.js";
import { deriveTableId } from "../rtable/hash.js";
import { RTableViewImpl } from "../rtable/view.js";
import { RTABLE_GROUP_TYPE_ID } from "./payload.js";
import { TableScope, deriveCreateMeta, deriveEnvelopeMeta, deriveBundleMeta } from "./scopes.js";
import { validateTableGroupPayload } from "./validate_ops.js";
import { evaluatePredicate, explainRowOpRestriction, explainRowOpFKReach } from "./predicates.js";
import { RTableGroupViewImpl } from "./view.js";
import { RTableGroupDeltaAccumulator, computeRTableGroupDelta, } from "./delta.js";
export { RTABLE_GROUP_TYPE_ID } from "./payload.js";
export const rTableGroupFactory = {
    computeRootObjectId: (payload, ctx) => __awaiter(void 0, void 0, void 0, function* () {
        const entry = dag.createEntry(payload, {}, position(), ctx.getCrypto().hash(HASH_SHA256));
        return entry.hash;
    }),
    validateCreationPayload: (payload, ctx, parent) => __awaiter(void 0, void 0, void 0, function* () {
        if (parent !== undefined)
            return validationFailure("table groups are root objects");
        return validateTableGroupPayload(payload, { mode: 'create', ctx });
    }),
    executeCreationPayload: (payload, ctx, scopedDag) => __awaiter(void 0, void 0, void 0, function* () {
        const create = payload;
        // the create entry carries each initial table's rows / pub meta, so
        // table scopes surface their genesis rows
        const schemaObj = yield ctx.getObject(create.schemaRef);
        if (schemaObj === undefined) {
            throw new Error(`RSchema '${create.schemaRef}' is not present in the replica`);
        }
        const pinned = version(...json.fromSet(create.schemaVersion));
        const schemaView = yield schemaObj.getView(pinned, pinned);
        return yield scopedDag.append(create, deriveCreateMeta(create, schemaView), position());
    }),
    loadObject: (id, ctx, opts) => __awaiter(void 0, void 0, void 0, function* () {
        var _b;
        if ((opts === null || opts === void 0 ? void 0 : opts.parent) !== undefined) {
            throw new Error("RTableGroup is a standalone object (no nesting parent)");
        }
        const backendLabel = (_b = opts === null || opts === void 0 ? void 0 : opts.backendLabel) !== null && _b !== void 0 ? _b : 'default';
        const rawDag = yield ctx.getDag(id, backendLabel);
        if (rawDag === undefined)
            throw new Error(`DAG '${id}' not found`);
        const scopedDag = new RootScopedDag(rawDag);
        const createOp = (yield scopedDag.loadEntry(id)).payload;
        return new RTableGroupImpl(id, createOp, ctx, backendLabel);
    }),
};
export class RTableGroupImpl {
    constructor(createOpId, createOp, ctx, backendLabel = 'default') {
        this.tables = new Map();
        // The void-recursion cycle guard. Voiding can recurse: a restriction/exists
        // (a caps insert gated by exists over caps, whose witness insert is itself
        // gated, ...) AND FK reach (a write whose FK target's own
        // liveness depends on another voided write). Both fold into ONE least-
        // fixpoint: a cycle DENIES (the ENTIRE cycle is treated as VOID). This makes
        // a self-granting op (its own witness) void — authority must root at a
        // genesis fiat row, which is never voided — and an FK reference cycle resolve
        // to DENY (replacing the former assume-live greatest-fixpoint FK guard).
        //
        // Deny-the-whole-cycle is the basic, replica-convergent semantics: it is a
        // pure function of the entry and `from`, so every replica agrees regardless
        // of which node a top-level query first touches. A delete-vs-delete negation
        // cycle (mutual revocation) therefore resolves to BOTH deletes voided (both
        // caps survive) — the conservative least fixpoint, not a single survivor.
        // NOTE: there is deliberately NO memo cache here. A cache keyed only by
        // (entry, from) would leak a traversal-dependent intermediate verdict across
        // independent top-level queries and break replica convergence; the guard
        // below is transient (added before recursing, removed in finally), so it
        // only ever detects a cycle within ONE computation. See VOID_SEMANTICS.md
        // for the logic-program framing and the future stratified/seniority design.
        this._voidVisiting = new Set();
        this.deltaStrategy = 'bounded';
        this.createOpId = createOpId;
        this.createOp = createOp;
        this.ctx = ctx;
        this.backendLabel = backendLabel;
    }
    getId() { return this.createOpId; }
    getType() { return _a.typeId; }
    getBackendLabel() { return this.backendLabel; }
    getContext() { return this.ctx; }
    // Create-time facts
    getName() { return this.createOp.name; }
    seed() { return this.createOp.seed; }
    hashAlgorithm() { return this.createOp.hashAlgorithm; }
    getSchemaRef() { return this.createOp.schemaRef; }
    getPinnedSchemaVersion() {
        return version(...json.fromSet(this.createOp.schemaVersion));
    }
    getBindings() {
        var _b;
        return Object.assign({}, ((_b = this.createOp.bindings) !== null && _b !== void 0 ? _b : {}));
    }
    getCanDeploy() {
        return this.createOp.canDeploy;
    }
    getCanObserve() {
        return this.createOp.canObserve;
    }
    // The binding name a bound group id resolves to, via the injective inverse
    // of getBindings() (undefined if the id is not a bound group).
    bindingNameForId(refId) {
        if (this._bindingNameById === undefined) {
            this._bindingNameById = new Map();
            for (const [name, id] of Object.entries(this.getBindings())) {
                this._bindingNameById.set(id, name);
            }
        }
        return this._bindingNameById.get(refId);
    }
    // The canObserve gate for an observation of `refId` (a bound group id), or
    // undefined when the binding is ungated (observation needs no authority).
    observeGateFor(refId) {
        var _b;
        const name = this.bindingNameForId(refId);
        if (name === undefined)
            return undefined;
        return (_b = this.getCanObserve()) === null || _b === void 0 ? void 0 : _b[name];
    }
    getIdProvider() {
        return this.createOp.idProvider;
    }
    getHashSuite() {
        return this.ctx.getHashSuite();
    }
    selfValidate() {
        return this.ctx.getConfig().selfValidate || false;
    }
    // Schema observation
    getSchemaObject() {
        return __awaiter(this, void 0, void 0, function* () {
            const obj = yield this.ctx.getObject(this.getSchemaRef());
            if (obj === undefined) {
                // a missing referenced object is an infrastructure error
                throw new Error(`RSchema '${this.getSchemaRef()}' is not present in the replica`);
            }
            return obj;
        });
    }
    // The schema version observed at a group position: the pinned genesis
    // version unioned with ref-advances at or below `at` (widened by barriers
    // concurrent to `at` when observed from `from`). The union with the
    // pinned version makes the create itself an implicit first deploy.
    resolveSchemaVersion(at, from) {
        return __awaiter(this, void 0, void 0, function* () {
            const scopedDag = yield this.getScopedDag();
            const advanced = yield resolveRefVersionAtPosition(scopedDag, this.getSchemaRef(), at, from !== null && from !== void 0 ? from : at);
            const resolved = version(...this.getPinnedSchemaVersion());
            for (const hash of advanced)
                resolved.add(hash);
            return resolved;
        });
    }
    // The effective schema at a group position (RSchemaImpl caches the
    // resolution per normalized version).
    resolveSchemaView(at, from) {
        return __awaiter(this, void 0, void 0, function* () {
            const schema = yield this.getSchemaObject();
            const resolved = yield this.resolveSchemaVersion(at, from);
            return schema.getView(resolved, resolved);
        });
    }
    // Member access
    // Construct (and cache) the nested RTable without an existence check:
    // used by views anchored at arbitrary positions and by validation.
    makeTable(name) {
        let table = this.tables.get(name);
        if (table === undefined) {
            table = new RTableImpl(deriveTableId(this.createOpId, name), name, this);
            this.tables.set(name, table);
        }
        return table;
    }
    getTable(name) {
        return __awaiter(this, void 0, void 0, function* () {
            const scopedDag = yield this.getScopedDag();
            const schemaView = yield this.resolveSchemaView(yield scopedDag.getFrontier());
            if (!schemaView.hasTable(name)) {
                throw new Error(`Table '${name}' does not exist in the effective schema at the group frontier`);
            }
            return this.makeTable(name);
        });
    }
    // Cross-group resolution: a bound foreign group's member-table view at the
    // foreign version this group observes at (`at`, `from`). `at`/`from` are
    // THIS group's positions; the foreign version is resolved through
    // ref-advances of the foreign group id (observe ops), then the foreign
    // group resolves its OWN schema at that version. Returns undefined for an
    // unbound name or a table absent at the foreign version (a missing
    // reference: the caller treats the FK target / exists atom as not-live).
    // Throws only if the bound group OBJECT is not present in the replica.
    //
    // FK reach across the group boundary recurses through the foreign group's
    // own least-fixpoint void guard (each group's _voidVisiting self-
    // terminates), so a mutual A->B->A FK ring resolves to DENY without a
    // shared cross-group guard.
    //
    // `filterVoided` (view-time enforcement only): drop observations VOIDED at
    // this `from` from the observed-version fold (Layer 2 of the observe gate),
    // so a back-dated observation by a former principal contributes no foreign
    // state. Validation / view / authentication call sites leave it false (the
    // geometric resolution); only computeEntryVoided enables it. Note the
    // observed `from` stays geometric: voided-observe filtering is an `at`-fold
    // property (which versions participate), not a negative-evidence horizon.
    resolveForeignTableView(groupName_1, table_1, at_1, from_1) {
        return __awaiter(this, arguments, void 0, function* (groupName, table, at, from, filterVoided = false) {
            const groupId = this.getBindings()[groupName];
            if (groupId === undefined)
                return undefined; // unbound name
            const foreign = yield this.loadForeignGroup(groupId, groupName);
            const dag = yield this.getScopedDag();
            const isLive = filterVoided ? (h) => this.isObserveLive(groupId, h, from) : undefined;
            const foreignAt = yield resolveRefVersionAtPosition(dag, groupId, at, from, isLive);
            const foreignFrom = yield resolveRefVersionAtPosition(dag, groupId, from, from);
            const foreignSchema = yield foreign.resolveSchemaView(foreignAt, foreignFrom);
            if (!foreignSchema.hasTable(table))
                return undefined; // missing table
            return new RTableViewImpl(foreign.makeTable(table), foreignAt, foreignFrom);
        });
    }
    // Whether the observation entry `entryHash` (a ref-advance of bound group
    // `groupId`) is LIVE at this `from` horizon: ungated bindings are always
    // live; a gated binding consults the at-use observe gate (the negation of
    // isEntryVoided's verdict for the observe). Used as the `isLive` filter for
    // the Layer 2 observed-version fold.
    isObserveLive(groupId, entryHash, from) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.observeGateFor(groupId) === undefined)
                return true; // ungated
            return !(yield this.isEntryVoided(entryHash, from));
        });
    }
    // Evaluate a binding's canObserve gate in the OBSERVED group's frame
    // (frame rebasing): the gate's exists / $author atoms read the foreign
    // group's tables at the observed foreign version (refAt, refFrom). 'object'
    // context (no subject row). Returns true when the binding is ungated. Both
    // the validation path and the at-use path call this with their own anchors.
    evaluateObserveGate(refId, author, refAt, refFrom) {
        return __awaiter(this, void 0, void 0, function* () {
            const gate = this.observeGateFor(refId);
            if (gate === undefined)
                return true; // ungated binding
            const foreign = yield this.loadForeignGroup(refId, this.bindingNameForId(refId));
            return evaluatePredicate(gate, {
                getTableView: (table) => foreign.makeTable(table).getView(refAt, refFrom),
                getForeignTableView: (groupName, table) => foreign.resolveForeignTableView(groupName, table, refAt, refFrom),
                author,
                context: 'object',
            });
        });
    }
    // G-upward filtered widening of the observed version for the at-use observe
    // gate (Layer 1). Base is the causal (geometric) version published by the
    // observe at `opPos`; concurrent observation barriers widen it ONLY when
    // their published version STRICTLY dominates the base in the foreign DAG
    // AND they are themselves live. Strict G-domination makes the recursion
    // ascend the foreign version, so it is acyclic and terminating by
    // construction (no back-edge: an equal/below or concurrent observe is never
    // a widening candidate), and benign G-incomparable concurrent observes
    // never recurse into each other. A negative edge (a revoke of this observe's
    // author) rides a strictly-dominating version under use-before-revoke, so
    // restricting to G-upward loses no security-relevant widening.
    resolveObserveGateRefAt(refId, opPos, from) {
        return __awaiter(this, void 0, void 0, function* () {
            const dag = yield this.getScopedDag();
            const base = yield resolveRefVersionAtPosition(dag, refId, opPos, opPos); // causal base (no widening)
            const foreignDag = yield this.getForeignGroupCausalDag(refId);
            const refAt = version(...base);
            const concurrent = yield findConcurrentRefAdvanceBarriers(dag, refId, opPos, from);
            for (const z of concurrent) {
                const entry = yield dag.loadEntry(z);
                if (entry === undefined || !isRefAdvancePayload(entry.payload))
                    continue;
                const vz = extractRefVersion(entry.payload);
                // strictly G-above the base: vz >= base AND base !>= vz
                const above = yield refVersionAtOrAbove(foreignDag, vz, base);
                if (!above)
                    continue;
                const below = yield refVersionAtOrAbove(foreignDag, base, vz);
                if (below)
                    continue; // equal / not strict -> not a widening candidate
                if (yield this.isEntryVoided(z, from))
                    continue; // skip voided concurrent observes
                for (const h of vz)
                    refAt.add(h);
            }
            return refAt;
        });
    }
    loadForeignGroup(groupId, groupName) {
        return __awaiter(this, void 0, void 0, function* () {
            const obj = yield this.ctx.getObject(groupId);
            if (obj === undefined) {
                const label = groupName !== undefined ? `'${groupName}' -> '${groupId}'` : `'${groupId}'`;
                throw new Error(`Bound group ${label} is not present in the replica`);
            }
            return obj;
        });
    }
    // The causal DAG of a bound foreign group, for ref-advance monotonicity
    // (observe validation). A missing object throws (infrastructure error).
    getForeignGroupCausalDag(groupId) {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield this.loadForeignGroup(groupId)).getCausalDag();
        });
    }
    // The foreign version this group observes for a bound group at (at, from):
    // the resolved ref-advance version on the group DAG. Used by the group VIEW
    // (resolveRefVersion) and by cross-group resolution.
    resolveObservedForeignVersion(groupId, at, from) {
        return __awaiter(this, void 0, void 0, function* () {
            const dag = yield this.getScopedDag();
            return resolveRefVersionAtPosition(dag, groupId, at, from);
        });
    }
    // KeyLookup: the publicKey registered for `keyId` through this group's
    // selected identity provider, anchored at group position `at`, LIVENESS-
    // BYPASSED (a raw provider read; see RTableViewImpl.rawProviderPublicKey).
    // Signature verification calls this at the op's own (at, at) position.
    //   - no provider configured            -> undefined (caller: no authentication)
    //   - local provider                    -> raw read of the local provider table
    //   - 'group.table' provider PRESENT but
    //     key absent / table not a provider  -> undefined (caller: fail-closed reject)
    //   - bound provider OBJECT not present  -> THROWS (caller/sync: defer)
    // The verdict is monotone: registration is causal-permanent and read past
    // liveness, so a key resolvable at `at` stays resolvable.
    resolveAuthorKey(keyId, at) {
        return __awaiter(this, void 0, void 0, function* () {
            const providerRef = this.getIdProvider();
            if (providerRef === undefined)
                return undefined;
            const [groupName, table] = splitTableRef(providerRef);
            if (groupName === undefined) {
                return new RTableViewImpl(this.makeTable(table), at, at).rawProviderPublicKey(keyId);
            }
            const groupId = this.getBindings()[groupName];
            if (groupId === undefined)
                return undefined; // unbound (create-time validated; defensive)
            const foreign = yield this.loadForeignGroup(groupId, groupName); // missing object -> throw -> defer
            const foreignAt = yield this.resolveObservedForeignVersion(groupId, at, at);
            return new RTableViewImpl(foreign.makeTable(table), foreignAt, foreignAt).rawProviderPublicKey(keyId);
        });
    }
    // Deploy: THE schema deploy moment — a barrier ref-advance of the schema
    // ref. Monotonicity (and at-or-above-pinned) validated; when authored, the
    // deploy signature is verified at validation (the group's own provider) and
    // the group's canDeploy predicate is evaluated against the verified author.
    deploy(refVersion, author, at) {
        return __awaiter(this, void 0, void 0, function* () {
            const scopedDag = yield this.getScopedDag();
            at = at !== null && at !== void 0 ? at : yield scopedDag.getFrontier();
            if (this.getCanDeploy() !== undefined && author === undefined) {
                throw new Error("deploy must be authored when the group declares canDeploy");
            }
            const { payload: base, meta } = prepareRefAdvance(this.getSchemaRef(), refVersion);
            const payload = author !== undefined
                ? yield signPayloadHelper(base, author)
                : base;
            if (this.selfValidate()) {
                const result = yield this.validatePayload(payload, at);
                if (!result.valid) {
                    throw new ValidationRejectedError(formatValidationFailure(result.why), result.why);
                }
            }
            return scopedDag.append(payload, meta, at);
        });
    }
    // Observe a bound foreign group at `refVersion`: a BARRIER ref-advance of
    // that group's id. This is how cross-group FK / exists targets
    // become visible — the dependent observes the foreign group at a bounded
    // version, and resolves `group.table` through it (see
    // resolveForeignTableView). Barrier: an observation concurrent to a use,
    // visible from the view's `from`, widens the observed foreign version at
    // the merged frontier, so a concurrent foreign revoke / schema deploy voids
    // the cross-group use there (symmetric with intra-group concurrent barrier
    // revoke; the foreign-version resolver already passes (at, from), so this
    // is purely the barrier tag). `group` is a binding name or a bound group id.
    //
    // When the observed binding declares a canObserve gate, the observation
    // must be authored: the signature is verified and the gate evaluated at
    // validation (and re-evaluated at-use), exactly like a gated deploy.
    observe(group, refVersion, author, at) {
        return __awaiter(this, void 0, void 0, function* () {
            const scopedDag = yield this.getScopedDag();
            at = at !== null && at !== void 0 ? at : yield scopedDag.getFrontier();
            const groupId = this.resolveBoundGroupId(group);
            if (this.observeGateFor(groupId) !== undefined && author === undefined) {
                throw new Error("observe must be authored when the binding declares canObserve");
            }
            const base = createRefAdvancePayload(groupId, refVersion);
            const payload = author !== undefined
                ? yield signPayloadHelper(base, author)
                : base;
            const meta = createRefAdvanceMeta(groupId); // barrier (default)
            if (this.selfValidate()) {
                const result = yield this.validatePayload(payload, at);
                if (!result.valid) {
                    throw new ValidationRejectedError(formatValidationFailure(result.why), result.why);
                }
            }
            return scopedDag.append(payload, meta, at);
        });
    }
    resolveBoundGroupId(group) {
        const bindings = this.getBindings();
        if (Object.prototype.hasOwnProperty.call(bindings, group))
            return bindings[group];
        if (Object.values(bindings).includes(group))
            return group;
        throw new Error(`'${group}' is not a bound group of this RTableGroup`);
    }
    // Single-entry atomic multi-table write. `writes` is ORDERED (the bundle
    // order, carried explicitly because entry hashing sorts map keys): op i's
    // FK conditions are checked at the sequential cut `at` ∪ earlier ops.
    // `author` signs every op; the whole entry validates and applies as a
    // unit (all-or-nothing).
    bundle(writes, author, at) {
        return __awaiter(this, void 0, void 0, function* () {
            const scopedDag = yield this.getScopedDag();
            at = at !== null && at !== void 0 ? at : yield scopedDag.getFrontier();
            const signed = [];
            for (const write of writes) {
                const op = author !== undefined
                    ? yield signPayloadHelper(write.op, author)
                    : write.op;
                signed.push({ table: write.table, op });
            }
            const payload = { action: 'bundle', writes: signed };
            const result = yield this.validatePayload(payload, at);
            if (!result.valid) {
                throw new ValidationRejectedError(formatValidationFailure(result.why), result.why);
            }
            const schemaView = yield this.resolveSchemaView(at);
            return scopedDag.append(payload, deriveBundleMeta(payload, schemaView), at);
        });
    }
    // Whether an entry is VOID at this `from` horizon: row restrictions already
    // passed hard validation at the parent frontier, but are rechecked here at
    // the op's own position observed from `from` so concurrent barrier revokes
    // or schema/observation revisions can still void the entry. Written FK
    // columns use the same view-time path. Bundles are all-or-nothing. The
    // genesis create entry is fiat (never voided);
    // ref-advances carry no row restrictions. Memoized per (entry, from);
    // a cycle on the authorization-recursion stack DENIES (treated as voided —
    // least fixpoint; see the _voidVisiting note above).
    isEntryVoided(entryHash, from) {
        return __awaiter(this, void 0, void 0, function* () {
            const key = entryHash + '|' + [...from].sort().join(',');
            // A cycle on the authorization-recursion stack: DENY (least fixpoint —
            // the whole cycle is treated as voided). The guard is transient, so
            // this only fires within one top-level computation (see the field note).
            if (this._voidVisiting.has(key))
                return true;
            this._voidVisiting.add(key);
            try {
                return (yield this.diagnoseEntryVoided(entryHash, from)) !== undefined;
            }
            finally {
                this._voidVisiting.delete(key);
            }
        });
    }
    explainEntryVoided(entryHash, from) {
        return __awaiter(this, void 0, void 0, function* () {
            const key = entryHash + '|' + [...from].sort().join(',');
            if (this._voidVisiting.has(key))
                return { kind: 'authorization-cycle' };
            this._voidVisiting.add(key);
            try {
                return yield this.diagnoseEntryVoided(entryHash, from);
            }
            finally {
                this._voidVisiting.delete(key);
            }
        });
    }
    diagnoseEntryVoided(entryHash, from) {
        return __awaiter(this, void 0, void 0, function* () {
            if (entryHash === this.createOpId)
                return undefined; // genesis fiat
            const scopedDag = yield this.getScopedDag();
            const entry = yield scopedDag.loadEntry(entryHash);
            if (entry === undefined)
                return undefined;
            const payload = entry.payload;
            if (isRefAdvancePayload(payload)) {
                return this.diagnoseObserveVoided(payload, entryHash, from);
            }
            const ops = [];
            const isBundle = payload['action'] === 'bundle';
            if (payload['action'] === 'row') {
                const envelope = payload;
                ops.push({ table: envelope.table, op: envelope.op });
            }
            else if (isBundle) {
                for (const write of payload.writes) {
                    ops.push({ table: write.table, op: write.op });
                }
            }
            else {
                return undefined; // create / unknown
            }
            const opPos = version(entryHash);
            const schemaView = yield this.resolveSchemaView(opPos, from);
            const getTableView = (table) => this.makeTable(table).getView(opPos, from);
            const getForeignTableView = (group, table) => this.resolveForeignTableView(group, table, opPos, from, true);
            const selfInserted = new Map();
            const selfDeleted = new Map();
            for (const { table, op } of ops) {
                const into = op.action === 'insert' ? selfInserted : op.action === 'delete' ? selfDeleted : undefined;
                if (into === undefined)
                    continue;
                if (!into.has(table))
                    into.set(table, new Set());
                into.get(table).add(op.rowId);
            }
            const localTargetProvided = (table, rowId) => {
                var _b, _c;
                if ((_b = selfDeleted.get(table)) === null || _b === void 0 ? void 0 : _b.has(rowId))
                    return false;
                if ((_c = selfInserted.get(table)) === null || _c === void 0 ? void 0 : _c.has(rowId))
                    return true;
                return undefined;
            };
            for (const [index, { table, op }] of ops.entries()) {
                const restrictionFailure = yield explainRowOpRestriction(op, table, schemaView, getTableView, getForeignTableView);
                if (restrictionFailure !== undefined) {
                    // row-not-live is an explain alias for restriction failure when
                    // enforced liveness is absent; it must not run before restriction
                    // diagnosis (valid deletes whose rules pass without a live target
                    // row would otherwise be voided incorrectly).
                    if ((op.action === 'update' || op.action === 'delete')
                        && localTargetProvided(table, op.rowId) !== true
                        && !(yield (yield getTableView(table)).hasRow(op.rowId))) {
                        const detail = {
                            kind: 'row-not-live',
                            table,
                            action: op.action,
                            rowId: op.rowId,
                        };
                        return isBundle ? { kind: 'bundle', index, detail } : detail;
                    }
                    const detail = {
                        kind: 'restriction',
                        table: restrictionFailure.table,
                        action: restrictionFailure.action,
                        rowId: restrictionFailure.rowId,
                        rule: restrictionFailure.rule,
                    };
                    return isBundle ? { kind: 'bundle', index, detail } : detail;
                }
                const fkFailure = yield explainRowOpFKReach(op, table, schemaView, getTableView, getForeignTableView, localTargetProvided);
                if (fkFailure !== undefined) {
                    const detail = {
                        kind: 'fk',
                        table: fkFailure.table,
                        action: fkFailure.action,
                        rowId: fkFailure.rowId,
                        column: fkFailure.column,
                        targetRef: fkFailure.targetRef,
                        targetRowId: fkFailure.targetRowId,
                    };
                    return isBundle ? { kind: 'bundle', index, detail } : detail;
                }
            }
            return undefined;
        });
    }
    diagnoseObserveVoided(payload, entryHash, from) {
        return __awaiter(this, void 0, void 0, function* () {
            const refId = payload.refId;
            if (refId === this.getSchemaRef())
                return undefined;
            const gate = this.observeGateFor(refId);
            if (gate === undefined)
                return undefined;
            const dag = yield this.getScopedDag();
            const opPos = version(entryHash);
            const refAt = yield this.resolveObserveGateRefAt(refId, opPos, from);
            const refFrom = yield resolveRefVersionAtPosition(dag, refId, from, from);
            const author = extractAuthor(payload);
            const ok = yield this.evaluateObserveGate(refId, author, refAt, refFrom);
            if (ok)
                return undefined;
            const binding = this.bindingNameForId(refId);
            return { kind: 'observe-gate', binding: binding !== null && binding !== void 0 ? binding : refId, rule: gate };
        });
    }
    // RObject interface
    validatePayload(payload, at) {
        return __awaiter(this, void 0, void 0, function* () {
            return validateTableGroupPayload(payload, { mode: 'op', group: this, at });
        });
    }
    applyPayload(payload, at) {
        return __awaiter(this, void 0, void 0, function* () {
            const scopedDag = yield this.getScopedDag();
            if (isRefAdvancePayload(payload)) {
                const refPayload = payload;
                // both ref-advance flavors are barriers: the schema
                // deploy AND a foreign-group observation revise the merged
                // frontier, so a concurrent deploy / foreign revoke voids a
                // concurrent use there (see observe + view.ts schemaView).
                return scopedDag.append(payload, createRefAdvanceMeta(refPayload.refId), at);
            }
            const action = payload['action'];
            if (action === 'row') {
                const envelope = payload;
                const schemaView = yield this.resolveSchemaView(at);
                return scopedDag.append(payload, deriveEnvelopeMeta(envelope, schemaView), at);
            }
            if (action === 'bundle') {
                const bundle = payload;
                const schemaView = yield this.resolveSchemaView(at);
                return scopedDag.append(payload, deriveBundleMeta(bundle, schemaView), at);
            }
            throw new Error("Invalid table group action in payload: " + action);
        });
    }
    getView(at, from) {
        return __awaiter(this, void 0, void 0, function* () {
            const scopedDag = yield this.getScopedDag();
            at = at !== null && at !== void 0 ? at : yield scopedDag.getFrontier();
            from = from !== null && from !== void 0 ? from : yield scopedDag.getFrontier();
            // the view resolves the schema eagerly: getSchemaView() is sync
            const schemaVersion = yield this.resolveSchemaVersion(at, from);
            const schema = yield this.getSchemaObject();
            const schemaView = yield schema.getView(schemaVersion, schemaVersion);
            return new RTableGroupViewImpl(this, at, from, schemaVersion, schemaView);
        });
    }
    // Delta strategy (see ./delta.ts): 'bounded' (default; projected-meet walk)
    // or 'full' (genesis recompute, the reference implementation for parity).
    setDeltaStrategy(strategy) {
        this.deltaStrategy = strategy;
    }
    // The objects this group OBSERVES — its RSchema plus every bound foreign
    // group — the referenced floors of the bounded revision bound (each can
    // revise an at-use verdict below the fork meet, so each is projected and
    // their GLB taken; see combineObserverRevisionBounds).
    getObservedObjects() {
        return __awaiter(this, void 0, void 0, function* () {
            const objects = [yield this.getSchemaObject()];
            for (const groupId of Object.values(this.getBindings())) {
                objects.push(yield this.loadForeignGroup(groupId));
            }
            return objects;
        });
    }
    // Root-only delta orchestration (bounds analysis + walk + compose). A
    // member table does not lead a delta; it participates via
    // createDeltaAccumulator (see RTableImpl).
    computeDelta(start, end) {
        return __awaiter(this, void 0, void 0, function* () {
            const rawDag = yield this.ctx.getDag(this.createOpId, this.backendLabel);
            if (rawDag === undefined)
                throw new Error(`DAG '${this.createOpId}' not found`);
            return computeRTableGroupDelta(this, rawDag, this.deltaStrategy, start, end);
        });
    }
    createDeltaAccumulator(start, end) {
        return new RTableGroupDeltaAccumulator(this, start, end);
    }
    extractForeignDeps(payload, _at) {
        var _b;
        if (isRefAdvancePayload(payload)) {
            const refPayload = payload;
            return [{
                    dagId: refPayload.refId,
                    requiredHashes: [...extractRefVersion(refPayload)],
                }];
        }
        const action = payload['action'];
        if (action === 'create') {
            const create = payload;
            const deps = [{
                    dagId: create.schemaRef,
                    requiredHashes: [...json.fromSet(create.schemaVersion)],
                }];
            for (const target of Object.values((_b = create.bindings) !== null && _b !== void 0 ? _b : {})) {
                deps.push({ dagId: target, requiredHashes: [] });
            }
            return deps;
        }
        // row / bundle ops validate against the schema at their position, and
        // may reference bound foreign groups through cross-group FK / exists
        // targets. The schema is needed at the op's version; bound groups need
        // only be present (their observed versions are pinned by the observe
        // ref-advance ops, which carry their own version deps above).
        const deps = [{ dagId: this.getSchemaRef(), requiredHashes: [] }];
        for (const target of Object.values(this.getBindings())) {
            deps.push({ dagId: target, requiredHashes: [] });
        }
        return deps;
    }
    subscribe(_callback) {
        throw new Error("Method not implemented.");
    }
    unsubscribe(_callback) {
        throw new Error("Method not implemented.");
    }
    getScopedDag() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._scopedDag === undefined) {
                const rawDag = yield this.ctx.getDag(this.createOpId, this.backendLabel);
                if (rawDag === undefined)
                    throw new Error(`DAG '${this.createOpId}' not found`);
                this._scopedDag = new RootScopedDag(rawDag);
            }
            return this._scopedDag;
        });
    }
    getCausalDag() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._causalDag === undefined) {
                const rawDag = yield this.ctx.getDag(this.createOpId, this.backendLabel);
                if (rawDag === undefined)
                    throw new Error(`DAG '${this.createOpId}' not found`);
                this._causalDag = rawDag;
            }
            return this._causalDag;
        });
    }
    // NestingParent interface
    getScopedDagForChild(childId) {
        return __awaiter(this, void 0, void 0, function* () {
            const name = yield this.tableNameForId(childId);
            const parentScopedDag = yield this.getScopedDag();
            return new NestedScopedDag(parentScopedDag, new TableScope(this, name));
        });
    }
    getCreationDagForChild(_childId, _at, _addPayload) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error("RTables are never created (they exist by schema)");
        });
    }
    tableNameForId(childId) {
        return __awaiter(this, void 0, void 0, function* () {
            // a cached table resolves directly; otherwise match against the
            // effective table set at the frontier
            for (const [name, table] of this.tables) {
                if (table.getId() === childId)
                    return name;
            }
            const scopedDag = yield this.getScopedDag();
            const schemaView = yield this.resolveSchemaView(yield scopedDag.getFrontier());
            for (const name of schemaView.getTableNames()) {
                if (deriveTableId(this.createOpId, name) === childId)
                    return name;
            }
            throw new Error(`No member table matches child id '${childId}'`);
        });
    }
    destroy() {
        return __awaiter(this, void 0, void 0, function* () {
            for (const table of this.tables.values()) {
                yield table.destroy();
            }
            this.tables.clear();
            this._scopedDag = undefined;
            this._causalDag = undefined;
        });
    }
}
_a = RTableGroupImpl;
RTableGroupImpl.create = (options) => __awaiter(void 0, void 0, void 0, function* () {
    const createPayload = {
        action: 'create',
        type: RTABLE_GROUP_TYPE_ID,
        name: options.name,
        seed: options.seed,
        schemaRef: options.schemaRef,
        schemaVersion: json.toSet([...options.schemaVersion]),
    };
    if (options.initialRows !== undefined)
        createPayload.initialRows = options.initialRows;
    if (options.bindings !== undefined)
        createPayload.bindings = options.bindings;
    if (options.canDeploy !== undefined)
        createPayload.canDeploy = options.canDeploy;
    if (options.canObserve !== undefined)
        createPayload.canObserve = options.canObserve;
    if (options.idProvider !== undefined)
        createPayload.idProvider = options.idProvider;
    if (options.hashAlgorithm !== undefined)
        createPayload.hashAlgorithm = options.hashAlgorithm;
    return createPayload;
});
RTableGroupImpl.typeId = RTABLE_GROUP_TYPE_ID;
export { RTableGroupViewImpl } from "./view.js";
